import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Check, 
  X, 
  Eye,
  Search,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  User,
  Calendar
} from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Badge } from '../../components/ui/Badge';
import { Modal } from '../../components/ui/Modal';
import { api } from '../../lib/api';
import { ENDPOINTS } from '../../config/api';
import { formatCurrency } from '../../lib/utils';

const formatDate = (date: string) => {
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(date));
};

interface BenefitSubscription {
  id: string;
  status: string;
  createdAt: string;
  requestedAt: string;
  approvedAt?: string;
  activatedAt?: string;
  cancelledAt?: string;
  monthlyAmount: string;
  subsidyAmount: string;
  netAmount: string;
  rejectionReason?: string;
  plan: {
    id: string;
    name: string;
    category: string;
  };
  user: {
    id: string;
    name: string;
    email: string;
    type: string;
  };
  dependents?: Array<{
    id: string;
    name: string;
    relationship: string;
  }>;
}

const statusConfig: Record<string, { label: string; variant: 'success' | 'warning' | 'error' | 'info'; icon: React.ReactNode }> = {
  PENDING_APPROVAL: { label: 'Aguardando Aprovação', variant: 'warning', icon: <Clock className="w-4 h-4" /> },
  ACTIVE: { label: 'Ativa', variant: 'success', icon: <CheckCircle className="w-4 h-4" /> },
  SUSPENDED: { label: 'Suspensa', variant: 'error', icon: <AlertCircle className="w-4 h-4" /> },
  CANCELLED: { label: 'Cancelada', variant: 'error', icon: <XCircle className="w-4 h-4" /> },
  EXPIRED: { label: 'Expirada', variant: 'info', icon: <Clock className="w-4 h-4" /> },
};

const userTypeLabels: Record<string, string> = {
  EMPLOYEE: 'Funcionário',
  CUSTOMER: 'Cliente',
  SUPPLIER: 'Fornecedor',
  ADMIN: 'Administrador',
};

export function BenefitSubscriptions() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedSubscription, setSelectedSubscription] = useState<BenefitSubscription | null>(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [isRejectOpen, setIsRejectOpen] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');

  const { data: subscriptionsData, isLoading } = useQuery({
    queryKey: ['admin-benefit-subscriptions', search, statusFilter],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (statusFilter) params.append('status', statusFilter);
      const response = await api.get(`${ENDPOINTS.benefits.adminSubscriptions}?${params}`);
      return response.data;
    },
  });

  // Extract subscriptions array from response
  const subscriptions: BenefitSubscription[] = subscriptionsData?.subscriptions || subscriptionsData?.data || (Array.isArray(subscriptionsData) ? subscriptionsData : []);

  const approveMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await api.post(ENDPOINTS.benefits.adminApprove(id));
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-benefit-subscriptions'] });
      setIsDetailsOpen(false);
    },
  });

  const rejectMutation = useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason: string }) => {
      const response = await api.post(ENDPOINTS.benefits.adminReject(id), { reason });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-benefit-subscriptions'] });
      setIsRejectOpen(false);
      setIsDetailsOpen(false);
      setRejectionReason('');
    },
  });

  const handleViewDetails = (subscription: BenefitSubscription) => {
    setSelectedSubscription(subscription);
    setIsDetailsOpen(true);
  };

  const handleApprove = (id: string) => {
    approveMutation.mutate(id);
  };

  const handleOpenReject = () => {
    setIsRejectOpen(true);
  };

  const handleReject = () => {
    if (selectedSubscription && rejectionReason) {
      rejectMutation.mutate({ id: selectedSubscription.id, reason: rejectionReason });
    }
  };

  const pendingCount = subscriptions.filter(s => s.status === 'PENDING_APPROVAL').length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white">Adesões a Benefícios</h1>
        <p className="text-surface-400 mt-1">
          Gerencie as solicitações de adesão aos planos de benefícios
        </p>
      </div>

      {/* Stats */}
      {pendingCount > 0 && (
        <Card className="bg-yellow-500/10 border-yellow-500/30">
          <div className="flex items-center gap-3">
            <Clock className="w-6 h-6 text-yellow-400" />
            <div>
              <p className="text-yellow-400 font-medium">
                {pendingCount} {pendingCount === 1 ? 'adesão aguardando' : 'adesões aguardando'} aprovação
              </p>
              <p className="text-sm text-yellow-400/70">
                Revise e aprove ou rejeite as solicitações pendentes
              </p>
            </div>
          </div>
        </Card>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-4 items-end">
        <div className="flex-1 min-w-[200px]">
          <Input
            placeholder="Buscar por usuário ou plano..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            leftIcon={<Search className="w-4 h-4" />}
          />
        </div>
        <Select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          options={[
            { value: '', label: 'Todos os status' },
            { value: 'PENDING_APPROVAL', label: 'Aguardando Aprovação' },
            { value: 'ACTIVE', label: 'Ativas' },
            { value: 'SUSPENDED', label: 'Suspensas' },
            { value: 'CANCELLED', label: 'Canceladas' },
          ]}
          className="w-48"
        />
      </div>

      {/* Table */}
      <Card>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-surface-700">
                <th className="text-left py-3 px-4 text-surface-400 font-medium">Usuário</th>
                <th className="text-left py-3 px-4 text-surface-400 font-medium">Plano</th>
                <th className="text-left py-3 px-4 text-surface-400 font-medium">Valor</th>
                <th className="text-left py-3 px-4 text-surface-400 font-medium">Status</th>
                <th className="text-left py-3 px-4 text-surface-400 font-medium">Data</th>
                <th className="text-right py-3 px-4 text-surface-400 font-medium">Ações</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={i} className="border-b border-surface-800">
                    {Array.from({ length: 6 }).map((_, j) => (
                      <td key={j} className="py-3 px-4">
                        <div className="h-4 bg-surface-700 rounded animate-pulse" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : subscriptions.length === 0 ? (
                <tr>
                  <td colSpan={6} className="py-12 text-center text-surface-400">
                    Nenhuma adesão encontrada
                  </td>
                </tr>
              ) : (
                subscriptions.map((sub: BenefitSubscription) => (
                  <tr key={sub.id} className="border-b border-surface-800 hover:bg-surface-800/50">
                    <td className="py-3 px-4">
                      <div>
                        <p className="text-white font-medium">{sub.user.name}</p>
                        <p className="text-surface-400 text-sm">{sub.user.email}</p>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <p className="text-white">{sub.plan.name}</p>
                      <p className="text-surface-400 text-sm">{sub.plan.category}</p>
                    </td>
                    <td className="py-3 px-4 text-white">
                      {formatCurrency(Number(sub.netAmount) / 100)}/mês
                    </td>
                    <td className="py-3 px-4">
                      <Badge variant={statusConfig[sub.status]?.variant || 'info'}>
                        {statusConfig[sub.status]?.label || sub.status}
                      </Badge>
                    </td>
                    <td className="py-3 px-4 text-surface-400">
                      {new Date(sub.createdAt).toLocaleDateString('pt-BR')}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleViewDetails(sub)}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        {sub.status === 'PENDING_APPROVAL' && (
                          <>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleApprove(sub.id)}
                              className="text-green-400 hover:text-green-300"
                            >
                              <Check className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setSelectedSubscription(sub);
                                handleOpenReject();
                              }}
                              className="text-red-400 hover:text-red-300"
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Details Modal */}
      <Modal
        isOpen={isDetailsOpen}
        onClose={() => setIsDetailsOpen(false)}
        title="Detalhes da Adesão"
        size="lg"
      >
        {selectedSubscription && (
          <div className="space-y-6">
            {/* User Info */}
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-primary-500/20 flex items-center justify-center">
                <User className="w-6 h-6 text-primary-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">
                  {selectedSubscription.user.name}
                </h3>
                <p className="text-surface-400">{selectedSubscription.user.email}</p>
                <Badge variant="info" size="sm" className="mt-1">
                  {userTypeLabels[selectedSubscription.user.type] || selectedSubscription.user.type}
                </Badge>
              </div>
            </div>

            {/* Plan Info */}
            <div className="bg-surface-800 rounded-lg p-4">
              <h4 className="text-sm font-medium text-surface-400 mb-2">Plano Solicitado</h4>
              <p className="text-lg font-semibold text-white">{selectedSubscription.plan.name}</p>
              <p className="text-surface-400">{selectedSubscription.plan.category}</p>
            </div>

            {/* Values */}
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-surface-800 rounded-lg p-4">
                <p className="text-sm text-surface-400">Valor Bruto</p>
                <p className="text-lg font-semibold text-white">
                  {formatCurrency(Number(selectedSubscription.monthlyAmount) / 100)}
                </p>
              </div>
              <div className="bg-surface-800 rounded-lg p-4">
                <p className="text-sm text-surface-400">Subsídio</p>
                <p className="text-lg font-semibold text-green-400">
                  -{formatCurrency(Number(selectedSubscription.subsidyAmount) / 100)}
                </p>
              </div>
              <div className="bg-surface-800 rounded-lg p-4">
                <p className="text-sm text-surface-400">Valor Líquido</p>
                <p className="text-lg font-semibold text-primary-400">
                  {formatCurrency(Number(selectedSubscription.netAmount) / 100)}
                </p>
              </div>
            </div>

            {/* Dependents */}
            {selectedSubscription.dependents && selectedSubscription.dependents.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-surface-400 mb-2">Dependentes</h4>
                <div className="space-y-2">
                  {selectedSubscription.dependents.map((dep) => (
                    <div key={dep.id} className="flex items-center justify-between bg-surface-800 rounded-lg p-3">
                      <span className="text-white">{dep.name}</span>
                      <Badge variant="info" size="sm">{dep.relationship}</Badge>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Dates */}
            <div className="flex items-center gap-2 text-sm text-surface-400">
              <Calendar className="w-4 h-4" />
              <span>Solicitado em {formatDate(selectedSubscription.requestedAt)}</span>
            </div>

            {/* Status */}
            <div className="flex items-center gap-2">
              <span className="text-surface-400">Status:</span>
              {(() => {
                const config = statusConfig[selectedSubscription.status];
                return (
                  <Badge variant={config?.variant || 'info'} className="flex items-center gap-1">
                    {config?.icon}
                    {config?.label || selectedSubscription.status}
                  </Badge>
                );
              })()}
            </div>

            {/* Rejection Reason */}
            {selectedSubscription.rejectionReason && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                <p className="text-sm text-red-400 font-medium">Motivo da Rejeição:</p>
                <p className="text-red-300">{selectedSubscription.rejectionReason}</p>
              </div>
            )}

            {/* Actions */}
            {selectedSubscription.status === 'PENDING_APPROVAL' && (
              <div className="flex justify-end gap-3 pt-4 border-t border-surface-700">
                <Button
                  variant="danger"
                  onClick={handleOpenReject}
                >
                  <X className="w-4 h-4 mr-2" />
                  Rejeitar
                </Button>
                <Button
                  onClick={() => handleApprove(selectedSubscription.id)}
                  disabled={approveMutation.isPending}
                >
                  <Check className="w-4 h-4 mr-2" />
                  Aprovar
                </Button>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Reject Modal */}
      <Modal
        isOpen={isRejectOpen}
        onClose={() => {
          setIsRejectOpen(false);
          setRejectionReason('');
        }}
        title="Rejeitar Adesão"
      >
        <div className="space-y-4">
          <p className="text-surface-300">
            Informe o motivo da rejeição. O usuário será notificado.
          </p>
          <Input
            label="Motivo da Rejeição"
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            placeholder="Ex: Documentação incompleta"
            required
          />
          <div className="flex justify-end gap-3">
            <Button
              variant="secondary"
              onClick={() => {
                setIsRejectOpen(false);
                setRejectionReason('');
              }}
            >
              Cancelar
            </Button>
            <Button
              variant="danger"
              onClick={handleReject}
              disabled={!rejectionReason || rejectMutation.isPending}
            >
              Confirmar Rejeição
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default BenefitSubscriptions;
